import React, { useCallback, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  Checkbox,
} from "@fluentui/react-components";
import { useDropzone } from "react-dropzone";
import styled from "styled-components";
import { useVehicleBulkPrefillMutation } from "state/queries/useVehicleBulkPrefill";
import { DismissRegular } from "@fluentui/react-icons";

interface BulkPrefillDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const DropzoneContainer = styled.div<{ isDragActive: boolean }>`
  border: 2px dashed
    ${(props) =>
      props.isDragActive
        ? "var(--colorBrandBackground)"
        : "var(--colorNeutralStroke1)"};
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  background-color: ${(props) =>
    props.isDragActive
      ? "var(--colorNeutralBackground1Hover)"
      : "var(--colorNeutralBackground1)"};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--colorBrandBackground);
    background-color: var(--colorNeutralBackground1Hover);
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: var(--colorNeutralBackground2);
  border-radius: 6px;
  margin-top: 12px;
`;

const DownloadLink = styled.a`
  color: var(--colorBrandBackground);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: var(--colorNeutralBackground2);
  border-radius: 6px;
  margin-top: 12px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--colorNeutralBackground2Hover);
    text-decoration: underline;
  }
`;

export const BulkPrefillDialog: React.FC<BulkPrefillDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [resultFile, setResultFile] = useState<{
    url: string;
    name: string;
  } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [timeoutError, setTimeoutError] = useState(false);
  const [showCloseAlert, setShowCloseAlert] = useState(false);
  const [createMtplOffer, setCreateMtplOffer] = useState(false);
  const wasProcessingWhenClosed = useRef(false);

  const { mutate: bulkPrefill } = useVehicleBulkPrefillMutation();

  // Reset state when dialog is reopened after being closed during processing
  useEffect(() => {
    if (isOpen && wasProcessingWhenClosed.current) {
      setSelectedFile(null);
      setResultFile(null);
      setIsProcessing(false);
      setTimeoutError(false);
      setShowCloseAlert(false);
      setCreateMtplOffer(false);
      wasProcessingWhenClosed.current = false;
    }
  }, [isOpen]);

  // Convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result as string;
        // Remove data:...;base64, prefix
        const base64Data = base64String.split(",")[1];
        if (base64Data) {
          resolve(base64Data);
        } else {
          reject(new Error("Failed to convert file to base64"));
        }
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Dropzone configuration
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        if (!file) return;

        // Validate file type (Excel files)
        const validTypes = [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.ms-excel",
          ".xlsx",
          ".xls",
        ];
        const fileExtension = file.name.split(".").pop()?.toLowerCase();

        if (
          !validTypes.includes(file.type) &&
          fileExtension !== "xlsx" &&
          fileExtension !== "xls"
        ) {
          toast.error(
            t(
              "qtg.bulkPrefill.invalidFileType",
              "Please upload a valid Excel file (.xlsx or .xls)"
            )
          );
          return;
        }

        setSelectedFile(file);
        setResultFile(null); // Clear previous result
      }
    },
    [t]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
    },
    multiple: false,
  });

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error(
        t("qtg.bulkPrefill.noFileSelected", "Please select a file first")
      );
      return;
    }

    try {
      setIsProcessing(true);
      setTimeoutError(false);

      // Convert file to base64
      const base64Data = await fileToBase64(selectedFile);

      // Send to API
      bulkPrefill(
        {
          name: selectedFile.name,
          base64Str: base64Data,
          createMtplOffer: createMtplOffer,
        },
        {
          onSuccess: (response) => {
            setResultFile(response);
            toast.success(
              t(
                "qtg.bulkPrefill.success",
                "Bulk prefill completed successfully"
              )
            );
            setIsProcessing(false);
          },
          onError: (error: any) => {
            const status = error?.response?.status;
            const responseText = error?.response?.data;

            if (
              status === 500 &&
              typeof responseText === "string" &&
              responseText.includes("500 - The request timed out.")
            ) {
              setTimeoutError(true);
            } else {
              toast.error(
                t(
                  "qtg.bulkPrefill.error",
                  "Error processing file. Please try again."
                )
              );
            }
            setIsProcessing(false);
          },
        }
      );
    } catch (error) {
      console.error("Error processing file:", error);
      toast.error(
        t(
          "qtg.bulkPrefill.processingError",
          "Error processing file. Please try again."
        )
      );
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    // If processing, show alert instead of closing
    if (isProcessing) {
      setShowCloseAlert(true);
      return;
    }

    setSelectedFile(null);
    setResultFile(null);
    setIsProcessing(false);
    setTimeoutError(false);
    setShowCloseAlert(false);
    setCreateMtplOffer(false);
    wasProcessingWhenClosed.current = false;
    onClose();
  };

  const handleForceClose = () => {
    // Track that we closed during processing
    if (isProcessing || timeoutError) {
      wasProcessingWhenClosed.current = true;
    }
    setSelectedFile(null);
    setResultFile(null);
    setIsProcessing(false);
    setTimeoutError(false);
    setShowCloseAlert(false);
    setCreateMtplOffer(false);
    onClose();
  };

  const handleDownload = () => {
    if (resultFile) {
      // Create a temporary anchor element to trigger download
      const link = document.createElement("a");
      link.href = resultFile.url;
      link.download = resultFile.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, data) => !data.open && handleClose()}
    >
      <DialogSurface style={{ maxWidth: "600px" }}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label={t("qtg.common.close", "Close")}
                icon={<DismissRegular />}
                onClick={handleClose}
              />
            }
          >
            {t("qtg.bulkPrefill.title", "Bulk Vehicle Prefill")}
          </DialogTitle>
          <DialogContent>
            {showCloseAlert && isProcessing && (
              <MessageBar intent="info" style={{ marginBottom: "16px" }}>
                <MessageBarBody>
                  {t(
                    "qtg.bulkPrefill.closeAlert",
                    "The operation is in progress. You can close the dialog and wait for an email with the results."
                  )}
                </MessageBarBody>
              </MessageBar>
            )}
            <div style={{ marginBottom: "16px" }}>
              {timeoutError ? (
                <div
                  style={{
                    padding: "16px",
                    backgroundColor: "var(--colorNeutralBackground2)",
                    borderRadius: "6px",
                    marginBottom: "16px",
                  }}
                >
                  <p
                    style={{
                      marginBottom: "12px",
                      color: "var(--colorNeutralForeground1)",
                      fontWeight: 600,
                    }}
                  >
                    {t(
                      "qtg.bulkPrefill.inProgress",
                      "The operation is in progress. You can close the dialog and wait for an email with the results."
                    )}
                  </p>
                  <p style={{ color: "var(--colorNeutralForeground2)" }}>
                    {t(
                      "qtg.bulkPrefill.emailNotification",
                      "The results will be sent to your email once processing is complete."
                    )}
                  </p>
                </div>
              ) : (
                <>
                  <p
                    style={{
                      marginBottom: "12px",
                      color: "var(--colorNeutralForeground2)",
                    }}
                  >
                    {isProcessing
                      ? t(
                          "qtg.bulkPrefill.processingDescription",
                          "The operation is in progress. You can close the dialog and wait for an email with the results."
                        )
                      : t(
                          "qtg.bulkPrefill.description",
                          "Upload an Excel file containing vehicle attributes to prefill multiple vehicles at once."
                        )}
                  </p>

                  {!resultFile && !isProcessing && (
                    <>
                      <DropzoneContainer
                        {...getRootProps()}
                        data-help="offers-bulk-prefill-dropzone"
                        isDragActive={isDragActive}
                      >
                        <input {...getInputProps()} />
                        {isDragActive ? (
                          <p>
                            {t(
                              "qtg.bulkPrefill.dropHere",
                              "Drop the Excel file here..."
                            )}
                          </p>
                        ) : (
                          <div>
                            <p style={{ marginBottom: "8px", fontWeight: 600 }}>
                              {t(
                                "qtg.bulkPrefill.dragDrop",
                                "Drag & drop an Excel file here"
                              )}
                            </p>
                            <p
                              style={{
                                color: "var(--colorNeutralForeground2)",
                              }}
                            >
                              {t(
                                "qtg.bulkPrefill.orClick",
                                "or click to browse"
                              )}
                            </p>
                          </div>
                        )}
                      </DropzoneContainer>

                      {selectedFile && (
                        <FileInfo>
                          <div>
                            <strong>{selectedFile.name}</strong>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "var(--colorNeutralForeground2)",
                              }}
                            >
                              {(selectedFile.size / 1024).toFixed(2)} KB
                            </div>
                          </div>
                          <Button
                            appearance="subtle"
                            icon={<DismissRegular />}
                            onClick={() => setSelectedFile(null)}
                            disabled={isProcessing}
                          />
                        </FileInfo>
                      )}

                      <div
                        data-help="offers-bulk-prefill-create-mtpl"
                        style={{ marginTop: "16px" }}
                      >
                        <Checkbox
                          checked={createMtplOffer}
                          onChange={(_, data) =>
                            setCreateMtplOffer(data.checked === true)
                          }
                          label={t(
                            "qtg.bulkPrefill.createMtplOffer",
                            "Create MTPL offers for vehicles"
                          )}
                        />
                      </div>
                    </>
                  )}

                  {resultFile && (
                    <div>
                      <p
                        style={{
                          marginBottom: "12px",
                          color: "var(--colorBrandForeground1)",
                          fontWeight: 600,
                        }}
                      >
                        {t(
                          "qtg.bulkPrefill.resultReady",
                          "Result file is ready for download:"
                        )}
                      </p>
                      <DownloadLink
                        href={resultFile.url}
                        download={resultFile.name}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        📥 {resultFile.name}
                      </DownloadLink>
                    </div>
                  )}
                </>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            {timeoutError ? (
              <Button appearance="primary" onClick={handleForceClose}>
                {t("qtg.common.ok", "OK")}
              </Button>
            ) : showCloseAlert && isProcessing ? (
              <>
                <Button appearance="secondary" onClick={handleForceClose}>
                  {t("qtg.common.close", "Close")}
                </Button>
                <Button
                  appearance="primary"
                  onClick={() => setShowCloseAlert(false)}
                >
                  {t("qtg.bulkPrefill.continueWaiting", "Continue Waiting")}
                </Button>
              </>
            ) : !resultFile ? (
              <Button
                data-help="offers-bulk-prefill-upload"
                appearance="primary"
                onClick={handleUpload}
                disabled={!selectedFile || isProcessing}
              >
                {isProcessing ? (
                  <>
                    <Spinner size="tiny" style={{ marginRight: "8px" }} />
                    {t("qtg.bulkPrefill.processing", "Processing...")}
                  </>
                ) : (
                  t("qtg.bulkPrefill.upload", "Upload & Process")
                )}
              </Button>
            ) : (
              <>
                <Button appearance="secondary" onClick={handleClose}>
                  {t("qtg.common.close", "Close")}
                </Button>
                <Button appearance="primary" onClick={handleDownload}>
                  {t("qtg.bulkPrefill.download", "Download Result")}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
